import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, KeyRound, Package, ShieldCheck, UserRound } from 'lucide-react';
import { TIMEZONES, countryProfile, supportedCountryForTimezone, type AddOnModule, type FacilityRequestDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, copyText } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Dialog } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Button, Checkbox, Field, Input, Select, Switch } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { AddOnToggles } from './AddOnToggles';
import { addOnStates, usableAddOns } from './add-ons';
import { PlanPicker, type PickablePlan } from './PlanPicker';
import { isValidTotp, TotpField } from './OwnerConfirm';
import { LimitFields, readLimits } from './TenantsPage';
import { safeReturnTo } from '../opd/BookAppointmentPage';

interface Plan extends PickablePlan {
  code: string;
}

interface ProvisionResultDto {
  tenantId: string;
  slug: string;
  temporaryPassword: string | null;
  credentialsEmailedTo: string | null;
}

const STEPS = ['Facility', 'Packages', 'Access', 'Super Admin'] as const;

const LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية' },
  { value: 'es', label: 'Español' },
  { value: 'ur-Latn', label: 'Roman Urdu' },
];

interface WizardForm {
  name: string;
  environment: 'live' | 'demo';
  locale: string;
  timezone: string;
  offlineEnabled: boolean;
  seedDemoData: boolean;
  adminName: string;
  adminUsername: string;
  adminEmail: string;
  price: string;
  billingCycle: string;
  setupFee: string;
  users: string;
  beds: string;
  branches: string;
  storageMb: string;
  smsCredits: string;
}

function initialForm(request: FacilityRequestDto | null): WizardForm {
  return {
    name: request?.name ?? '',
    environment: request?.environment ?? 'live',
    locale: 'en',
    timezone: 'Asia/Karachi',
    offlineEnabled: request?.offlineEnabled ?? false,
    seedDemoData: true,
    adminName: request?.contactName ?? '',
    adminUsername: '',
    adminEmail: request?.contactEmail ?? '',
    price: '0',
    billingCycle: 'monthly',
    setupFee: '0',
    users: '',
    beds: '',
    branches: '',
    storageMb: '',
    smsCredits: '',
  };
}

export function CredentialsResultDialog({ result, onDone }: { result: ProvisionResultDto; onDone: (tenantId: string) => void }) {
  return (
    <Dialog
      open
      dismissable={false}
      onClose={() => undefined}
      title="Facility provisioned"
      description="Copy what you need before closing — the temporary password is shown only here."
      footer={<Button onClick={() => onDone(result.tenantId)}>I have copied it — open the facility</Button>}
    >
      <div className="space-y-3">
        <Alert variant="success">Roles, departments, number series and message templates were seeded. The Super Admin can sign in now.</Alert>
        {result.credentialsEmailedTo ? (
          <Alert>
            The sign-in details were emailed to <span className="font-medium">{result.credentialsEmailedTo}</span>.
          </Alert>
        ) : (
          <Alert variant="warning">
            No email gateway is configured, so the details were not sent — pass the temporary password below to the Super Admin yourself.{' '}
            <Link to="/owner/alerts" className="font-medium underline">
              Set up SMTP
            </Link>
          </Alert>
        )}
        <div className="rounded-md border bg-muted/40 p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Organisation code at sign-in</p>
          <p className="mt-1 select-all font-mono text-sm">{result.slug}</p>
          <Button size="sm" variant="ghost" className="mt-1" onClick={() => void copyText(result.slug)}>
            Copy
          </Button>
        </div>
        {result.temporaryPassword && (
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Temporary password</p>
            <p className="mt-1 select-all font-mono text-sm">{result.temporaryPassword}</p>
            <Button size="sm" variant="ghost" className="mt-1" onClick={() => void copyText(result.temporaryPassword ?? '')}>
              Copy
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">The Super Admin must change it and enrol in two-factor at first sign-in.</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function StepIndicator({ step }: { step: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs">
      {STEPS.map((label, index) => {
        const done = index < step;
        const current = index === step;
        return (
          <li key={label} className="flex items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium',
                current && 'border-primary bg-primary/10 text-primary',
                done && 'border-transparent bg-muted text-muted-foreground',
                !current && !done && 'text-muted-foreground',
              )}
            >
              <span className="grid h-4 w-4 place-items-center rounded-full bg-background/60 text-[10px]">{done ? <Check className="h-3 w-3" aria-hidden /> : index + 1}</span>
              {label}
            </span>
            {index < STEPS.length - 1 && <span className="h-px w-4 bg-border" aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

export function ProvisionTenantPage() {
  const [params] = useSearchParams();
  const requestId = params.get('requestId');
  const returnTo = safeReturnTo(params.get('returnTo'), requestId ? '/owner/facility-requests' : '/owner/tenants');

  const request = useQuery({
    queryKey: ['facility-request', requestId],
    queryFn: () => get<FacilityRequestDto>(`/platform/facility-requests/${requestId}`),
    enabled: Boolean(requestId),
  });

  if (requestId && !request.data) {
    return (
      <FormScreen title="Provision a facility" icon={Building2} backTo={returnTo}>
        <RecordState error={request.error} />
      </FormScreen>
    );
  }

  return <ProvisionForm key={requestId ?? 'new'} request={request.data ?? null} returnTo={returnTo} />;
}

function ProvisionForm({ request, returnTo }: { request: FacilityRequestDto | null; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [initial] = useState(() => initialForm(request));
  const [form, setForm] = useState<WizardForm>(initial);
  const offeredLocales: string[] = countryProfile(supportedCountryForTimezone(form.timezone)).locales.ui;
  const [planIds, setPlanIds] = useState<string[]>(request?.planIds ?? []);
  const [addOns, setAddOns] = useState<AddOnModule[]>((request?.addOns ?? []) as AddOnModule[]);
  const [reason, setReason] = useState(request ? `Facility request ${request.id.slice(0, 8)}` : '');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisionResultDto | null>(null);

  const plans = useQuery({ queryKey: ['plans'], queryFn: () => get<Plan[]>('/platform/plans') });

  const provision = useMutation({
    mutationFn: () =>
      post<ProvisionResultDto>('/platform/tenants', {
        name: form.name.trim(),
        planIds,
        addOns: usableAddOns(plans.data, planIds, addOns),
        environment: form.environment,
        offlineEnabled: form.offlineEnabled,
        timezone: form.timezone,
        locale: form.locale,
        adminName: form.adminName.trim(),
        adminUsername: form.adminUsername,
        adminEmail: form.adminEmail || undefined,
        seedDemoData: form.seedDemoData,
        priceMinor: Math.round(Number(form.price || 0) * 100),
        billingCycle: form.billingCycle as 'monthly' | 'annual',
        setupFeeMinor: Math.round(Number(form.setupFee || 0) * 100),
        limits: readLimits(form as unknown as Record<string, unknown>),
        ...(request ? { requestId: request.id } : {}),
        totpCode,
        reason: reason.trim(),
      }),
    onSuccess: (created) => {
      setTotpCode('');
      toast.success('Facility provisioned', `${created.slug} is ready`);
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['facility-requests'] });
      setResult(created);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const stepValid = [
    form.name.trim().length >= 2,
    planIds.length > 0,
    true,
    form.adminName.trim().length >= 2 && form.adminUsername.length >= 3 && reason.trim().length >= 5 && isValidTotp(totpCode),
  ];
  const last = step === STEPS.length - 1;
  const dirty = !provision.isSuccess && (JSON.stringify(form) !== JSON.stringify(initial) || planIds.length > 0 || totpCode !== '');

  return (
    <FormScreen
      title={request ? `Provision from request: ${request.name}` : 'Provision a facility'}
      description={`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]}`}
      icon={Building2}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (!stepValid[step]) return;
        if (!last) {
          setStep(step + 1);
          return;
        }
        if (!stepValid.every(Boolean)) return;
        setError(null);
        provision.mutate();
      }}
    >
      <StepIndicator step={step} />

      {step === 0 && (
        <FormSection title="Facility" icon={Building2}>
          <Field label="Facility name" required className="sm:col-span-2">
            <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field
            label="Environment"
            required
            className="sm:col-span-2"
            hint="A demo is left out of revenue and renewals, cannot run offline and is deleted without a notice period."
          >
            <ChoiceButtons
              layout="cards"
              value={form.environment}
              onChange={(value) => setForm({ ...form, environment: value })}
              options={[
                { value: 'live', label: 'Live', hint: 'A paying customer' },
                { value: 'demo', label: 'Demo', hint: 'For showing or training' },
              ]}
            />
          </Field>
          <Field label="Language" hint="Staff can change their own language later">
            <ChoiceButtons
              layout="chips"
              value={form.locale}
              onChange={(value) => setForm({ ...form, locale: value })}
              options={LOCALES.filter((locale) => locale.value === 'en' || offeredLocales.includes(locale.value))}
            />
          </Field>
          <Field label="Time zone of the first branch" hint="The facility sets each branch's country itself">
            <Select value={form.timezone} onChange={(event) => setForm({ ...form, timezone: event.target.value })}>
              {TIMEZONES.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </Select>
          </Field>
        </FormSection>
      )}

      {step === 1 && (
        <FormSection title="Packages" icon={Package} columns={1}>
          <PlanPicker plans={plans.data} chosen={planIds} onChange={setPlanIds} />
          <div>
            <p className="text-sm font-medium">Add-on services</p>
            <AddOnToggles
              states={addOnStates(plans.data, planIds, addOns)}
              onToggle={(state) => setAddOns(state.on ? addOns.filter((code) => code !== state.code) : [...addOns, state.code])}
            />
          </div>
          <Checkbox
            checked={form.seedDemoData}
            onCheckedChange={(checked) => setForm({ ...form, seedDemoData: checked })}
            label="Seed a starter catalogue for the chosen packages"
          />
        </FormSection>
      )}

      {step === 2 && (
        <FormSection title="Access" icon={KeyRound} columns={1}>
          <Switch
            checked={form.offlineEnabled}
            onCheckedChange={(checked) => setForm({ ...form, offlineEnabled: checked })}
            disabled={form.environment === 'demo'}
            label="Allow offline mode"
            description="One server at the facility serves every branch; remote branches need a network link to it."
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Price">
              <Input type="number" step="0.01" min={0} value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} />
            </Field>
            <Field label="Billing cycle">
              <ChoiceButtons
                value={form.billingCycle}
                onChange={(value) => setForm({ ...form, billingCycle: value })}
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'annual', label: 'Annual' },
                ]}
              />
            </Field>
            <Field label="Setup fee">
              <Input type="number" step="0.01" min={0} value={form.setupFee} onChange={(event) => setForm({ ...form, setupFee: event.target.value })} />
            </Field>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">Limits (blank for unlimited)</p>
            <LimitFields form={form as unknown as Record<string, unknown>} onChange={(patch) => setForm({ ...form, ...patch })} />
          </div>
        </FormSection>
      )}

      {step === 3 && (
        <>
          <FormSection title="Super Admin" icon={UserRound}>
            <Field label="Super Admin name" required>
              <Input value={form.adminName} onChange={(event) => setForm({ ...form, adminName: event.target.value })} />
            </Field>
            <Field label="Super Admin username" required hint="Unique inside this facility">
              <Input
                value={form.adminUsername}
                onChange={(event) => setForm({ ...form, adminUsername: event.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                placeholder="admin"
              />
            </Field>
            <Field label="Super Admin email" hint="Optional — only used to send the first password" className="sm:col-span-2">
              <Input type="email" value={form.adminEmail} onChange={(event) => setForm({ ...form, adminEmail: event.target.value })} />
            </Field>
          </FormSection>
          <FormSection title="Confirm" icon={ShieldCheck} columns={1}>
            <Field label="Reason" required hint="Recorded in the platform audit trail — a ticket number is ideal.">
              <Input value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
            <TotpField value={totpCode} onChange={setTotpCode} />
            {error && <Alert variant="destructive">{error}</Alert>}
          </FormSection>
        </>
      )}

      <FormActions
        submitLabel={last ? 'Provision' : 'Next'}
        loading={provision.isPending}
        incomplete={!stepValid[step] || (last && !stepValid.every(Boolean))}
        cancelTo={returnTo}
        bleed={false}
        secondary={
          step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} disabled={provision.isPending}>
              Back
            </Button>
          ) : undefined
        }
      />

      {result && (
        <CredentialsResultDialog
          result={result}
          onDone={(tenantId) => {
            setResult(null);
            navigate(`/owner/tenants/${tenantId}`, { replace: true });
          }}
        />
      )}
    </FormScreen>
  );
}
